import React, { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from "react-router-dom";

import {
    setArticleOrderIndex,
    newArticle,
    setCollectionPublished,
    setCollectionName
} from '../api';

import {
    Article,
    Collection,
    RootStore
} from '../app/types';

interface SortableItemProps {
    article: Article;
    onClick: () => void;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    isDragging: boolean;
}

const SortableItem: FunctionComponent<SortableItemProps> = ({
    article,
    onClick,
    onDragStart,
    onDragOver,
    onDrop,
    isDragging
}) => (
    <div
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: isDragging ? '#f0f7ff' : '#fff',
            borderRadius: 8,
            marginBottom: 8,
            border: '1px solid #e0e0e0',
            cursor: 'grab',
            transition: 'all 0.15s ease',
        }}
    >
        <span style={{
            color: '#bbb',
            marginRight: 12,
            fontSize: '1.1rem',
            userSelect: 'none',
        }}>
            ⋮⋮
        </span>
        <span
            onClick={onClick}
            style={{
                flex: 1,
                fontWeight: 500,
                color: '#333',
                cursor: 'pointer',
            }}
        >
            {article.name}
        </span>
        <span style={{
            fontSize: '0.85rem',
            color: '#888',
            marginLeft: 16,
            minWidth: 80,
        }}>
            {(new Date(article.updated)).toLocaleDateString()}
        </span>
        <span style={{
            fontSize: '0.8rem',
            padding: '2px 8px',
            borderRadius: 12,
            marginLeft: 12,
            backgroundColor: article.published ? '#e8f5e9' : '#f5f5f5',
            color: article.published ? '#2e7d32' : '#888',
        }}>
            {article.published ? 'Published' : 'Draft'}
        </span>
        <span style={{
            fontSize: '0.8rem',
            color: '#aaa',
            marginLeft: 12,
            minWidth: 60,
            textAlign: 'right',
        }}>
            {(article.content.length / 1024).toFixed(1)}KB
        </span>
    </div>
);

export const CollectionEditor: FunctionComponent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [newArticleName, setNewArticleName] = useState("");
    const [name, setName] = useState("");
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const { collectionID } = useParams();
    const collectionIdNum = parseInt(collectionID);

    const allCollections = useSelector((state: RootStore) => state.collections);
    const allArticles = useSelector((state: RootStore) => state.articles);

    const collection = useMemo(() =>
        allCollections.find((c: Collection) => c.ID === collectionIdNum) ?? null,
        [allCollections, collectionIdNum]
    );

    const articles = useMemo(() =>
        allArticles
            .filter((a: Article) => a.collectionID === collectionIdNum)
            .sort((a: Article, b: Article) => a.orderIndex - b.orderIndex),
        [allArticles, collectionIdNum]
    );

    const hasNameChanges = collection && collection.name !== name;

    useEffect(() => {
        if (!name.length)
            setName(collection ? collection.name : "");
    }, [name.length, collection]);

    const handleDragStart = (index: number) => (e: React.DragEvent) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (newIndex: number) => (e: React.DragEvent) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === newIndex) return;

        const article = articles[draggedIndex];
        let newOrderIndex;
        if (newIndex === 0) {
            newOrderIndex = articles[0].orderIndex - 10000;
        } else if (newIndex === (articles.length - 1)) {
            newOrderIndex = articles[(articles.length - 1)].orderIndex + 10000;
        } else {
            newOrderIndex = (articles[newIndex - 1].orderIndex + articles[newIndex].orderIndex) / 2;
        }
        setArticleOrderIndex(dispatch, article.ID, newOrderIndex.toFixed(0));
        setDraggedIndex(null);
    };

    const onArticleClick = (article: Article) => {
        navigate("/article/" + article.ID + '/edit');
    };

    const onNewArticle = () => {
        newArticle(dispatch, collectionID, newArticleName)
            .then((article) => {
                setNewArticleName("");
                onArticleClick(article);
            });
    };

    const onSaveName = () => {
        setCollectionName(dispatch, collectionID, name);
    };

    const onTogglePublished = () => {
        setCollectionPublished(dispatch, collectionID, !collection.published);
    };

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
            {/* Header Section */}
            <div style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                padding: 24,
                marginBottom: 24,
                border: '1px solid #e0e0e0',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <input
                        id="title-input"
                        autoComplete="off"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Collection name"
                        style={{
                            flex: '1 1 300px',
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: 8,
                            outline: 'none',
                        }}
                    />
                    <button
                        id="title-save-button"
                        onClick={onSaveName}
                        disabled={!hasNameChanges}
                        style={{
                            padding: '10px 20px',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            border: 'none',
                            borderRadius: 8,
                            backgroundColor: hasNameChanges ? '#0d6efd' : '#e0e0e0',
                            color: hasNameChanges ? '#fff' : '#999',
                            cursor: hasNameChanges ? 'pointer' : 'not-allowed',
                        }}
                    >
                        {hasNameChanges ? 'Save' : 'Saved'}
                    </button>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 24,
                    marginTop: 16,
                    paddingTop: 16,
                    borderTop: '1px solid #eee',
                }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        color: '#555',
                    }}>
                        <input
                            type="checkbox"
                            checked={collection?.published || false}
                            onChange={onTogglePublished}
                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                        />
                        Published
                    </label>
                    <span style={{ color: '#888', fontSize: '0.9rem' }}>
                        {articles.length} article{articles.length !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>

            {/* Articles List */}
            <div style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                padding: 24,
                border: '1px solid #e0e0e0',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                        Articles
                    </h3>
                </div>

                {articles.length === 0 ? (
                    <div style={{
                        padding: 32,
                        textAlign: 'center',
                        color: '#999',
                        backgroundColor: '#fafafa',
                        borderRadius: 8,
                        border: '1px dashed #ddd',
                    }}>
                        No articles yet. Create your first one below.
                    </div>
                ) : (
                    <div>
                        {articles.map((article: Article, index: number) => (
                            <SortableItem
                                key={article.ID}
                                article={article}
                                onClick={() => onArticleClick(article)}
                                onDragStart={handleDragStart(index)}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop(index)}
                                isDragging={draggedIndex === index}
                            />
                        ))}
                    </div>
                )}

                {/* New Article Form */}
                <div style={{
                    display: 'flex',
                    gap: 12,
                    marginTop: 16,
                    paddingTop: 16,
                    borderTop: '1px solid #eee',
                }}>
                    <input
                        placeholder="New article name..."
                        value={newArticleName}
                        onChange={(e) => setNewArticleName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && newArticleName.length > 0) {
                                onNewArticle();
                            }
                        }}
                        style={{
                            flex: 1,
                            padding: '10px 14px',
                            fontSize: '0.95rem',
                            border: '1px solid #ddd',
                            borderRadius: 8,
                            outline: 'none',
                        }}
                    />
                    <button
                        disabled={newArticleName.length === 0}
                        onClick={onNewArticle}
                        style={{
                            padding: '10px 20px',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            border: 'none',
                            borderRadius: 8,
                            backgroundColor: newArticleName.length > 0 ? '#28a745' : '#e0e0e0',
                            color: newArticleName.length > 0 ? '#fff' : '#999',
                            cursor: newArticleName.length > 0 ? 'pointer' : 'not-allowed',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        + New Article
                    </button>
                </div>
            </div>
        </div>
    );
}
